import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { Send, MessageSquare, RotateCcw } from 'lucide-react';
import translationChatAPI from '../../services/translationChatService';
import TranslationTurn from './TranslationTurn';
import { useBrand } from '../../context/BrandContext';

const newSessionIds = () => ({
  threadId: `tcp_${crypto.randomUUID()}`,
  chatId: `tcp_${crypto.randomUUID()}`,
});

// The language list is whatever bi-pepper can round-trip for the active brand: translating the
// message to English needs an X->en (or unknown->en) agent, translating the bot's answer back needs
// an en->X agent — so the offered targets are the targets of the en->X agents.
const targetLanguagesFromAgents = (agents) =>
  [...new Set(
    (agents || [])
      .filter((a) => a.source_language === 'en')
      .map((a) => a.target_language),
  )].sort();

const TranslationChatPage = () => {
  const { activeBrand } = useBrand();
  const [languages, setLanguages] = useState([]);
  const [targetLanguage, setTargetLanguage] = useState('');
  const [turns, setTurns] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [agentsError, setAgentsError] = useState('');
  const sessionRef = useRef(newSessionIds());

  const loadLanguages = useCallback(async () => {
    setAgentsError('');
    try {
      const { data } = await translationChatAPI.listAgents();
      const targets = targetLanguagesFromAgents(data.agents);
      setLanguages(targets);
      setTargetLanguage((current) => {
        if (current && targets.includes(current)) return current;
        return targets.includes('de') ? 'de' : (targets[0] || '');
      });
    } catch (err) {
      setLanguages([]);
      setAgentsError(err.response?.data?.error || 'Failed to load available languages');
    }
  }, []);

  // Languages come from bi-pepper per brand — reload whenever the active brand changes. A brand
  // switch also resets the session: persisted turns live under the previous brand's thread.
  useEffect(() => {
    setTurns([]);
    setError('');
    sessionRef.current = newSessionIds();
    loadLanguages();
  }, [loadLanguages, activeBrand]);

  // A language that is no longer offered (brand switch, agent list changed) starts a fresh session.
  useEffect(() => {
    if (languages.length > 0 && targetLanguage && !languages.includes(targetLanguage)) {
      setTargetLanguage(languages.includes('de') ? 'de' : languages[0]);
      setTurns([]);
      sessionRef.current = newSessionIds();
    }
  }, [languages, targetLanguage]);

  // Switching language starts a fresh session — playground sessions are ephemeral by design
  // (see docs/superpowers/specs/2026-07-03-translation-chat-playground-design.md). The backend
  // persists turns per thread_id and derives history/checkpoint_id from them itself, so the
  // frontend only needs to track which thread_id/chat_id this session is using.
  const handleLanguageChange = (lang) => {
    setTargetLanguage(lang);
    setTurns([]);
    setError('');
    sessionRef.current = newSessionIds();
  };

  // Starts a fresh thread_id/chat_id without touching the selected language — lets staff reset a
  // conversation mid-test without losing their language choice, same reset handleLanguageChange
  // does as a side effect of switching languages.
  const handleNewSession = () => {
    setTurns([]);
    setError('');
    sessionRef.current = newSessionIds();
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending || !targetLanguage) return;
    setSending(true);
    setError('');

    try {
      const { data } = await translationChatAPI.sendMessage({
        target_language: targetLanguage,
        text,
        thread_id: sessionRef.current.threadId,
        chat_id: sessionRef.current.chatId,
      });
      setTurns((prev) => [...prev, data]);
      setInput('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const canSend = targetLanguage && !agentsError;

  return (
    <div className="max-w-4xl mx-auto py-6 flex flex-col h-[calc(100vh-3.5rem)]">
      <Card className="mb-4 shrink-0">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary-500" />
            <h2 className="text-xl font-bold">Chatbot</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              onClick={handleNewSession}
              disabled={sending || turns.length === 0}
            >
              <RotateCcw className="h-3.5 w-3.5" />New session
            </Button>
            <Select value={targetLanguage} onValueChange={handleLanguageChange}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder={languages.length === 0 ? 'No languages' : 'Language'} />
              </SelectTrigger>
              <SelectContent>
                {languages.map((code) => (
                  <SelectItem key={code} value={code}>{code.toUpperCase()}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="flex-1 overflow-y-auto space-y-4 px-1">
        {agentsError && (
          <Alert variant="error">
            <AlertDescription>{agentsError}</AlertDescription>
          </Alert>
        )}
        {turns.length === 0 && !agentsError && (
          <p className="text-sm text-ink-dim text-center py-10">
            Write a message in {targetLanguage ? targetLanguage.toUpperCase() : 'the target language'} to start testing translation.
          </p>
        )}
        {turns.map((turn, i) => (
          <TranslationTurn key={i} turn={turn} targetLanguage={targetLanguage} threadId={sessionRef.current.threadId} />
        ))}
        {sending && <div className="flex justify-center py-2"><Spinner size="sm" /></div>}
      </div>

      {error && (
        <Alert variant="error" className="mt-2 shrink-0">
          <AlertDescription>
            {error}
            <Button size="sm" variant="ghost" className="ml-2" onClick={handleSend}>Retry</Button>
          </AlertDescription>
        </Alert>
      )}

      <div className="mt-3 flex gap-2 items-end shrink-0">
        <Textarea
          rows={2}
          placeholder={`Type a message in ${targetLanguage ? targetLanguage.toUpperCase() : 'the target language'}...`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={sending || !canSend}
        />
        <Button onClick={handleSend} disabled={sending || !input.trim() || !canSend} className="gap-1.5">
          <Send className="h-4 w-4" />Send
        </Button>
      </div>
    </div>
  );
};

export default TranslationChatPage;
